import { useEffect, useState } from "react";
import { Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import useLoader from "../../hooks/useLoader";
import { getStates, getComplianceFormsByState } from "../../services/compliance";
import ShopsEstablishment from "./ShopsEstablishment";

// Presentation info for each compliance type returned by the API; keeps the
// card header styling/title stable while the forms underneath come from the
// backend for the selected state.
const CARD_META = {
    PT: { title: "PT - Professional Tax", form: "pt", cls: "bg-primary text-white" },
    LWF: { title: "LWF - Labour Welfare Fund", form: "lwf", cls: "bg-primary text-white" },
    // Real pms_compliance_form rows already exist for these two
    // (compliance_type "MW" / "LLF"), grouped by the loop above exactly like
    // PT/LWF - these two entries just give them a card title/style.
    MW: { title: "MW - Minimum Wages", form: "mw", cls: "bg-primary text-white" },
    LLF: { title: "LLF - Register of Wages / Form XVII", form: "llf", cls: "bg-primary text-white" },
};

const Compliances = () => {
    const { showLoader, hideLoader } = useLoader();

    const [states, setStates] = useState([]);
    const [selectedState, setSelectedState] = useState("");
    const [forms, setForms] = useState({});

    // Load states once and default to the first one.
    useEffect(() => {
        getStates()
            .then((res) => {
                const list = res.data?.data || [];
                setStates(list);
                if (list.length) {
                    setSelectedState(list[0]._ZoneID);
                }
            })
            .catch(() => toast.error("Failed to load states. Please try again."));
    }, []);

    // Whenever the selected state changes, fetch the compliance forms
    // configured for it, grouped by compliance type (PT, LWF, ...).
    useEffect(() => {
        if (!selectedState) return;

        showLoader();
        getComplianceFormsByState(selectedState)
            .then((res) => setForms(res.data?.data || {}))
            .catch(() => toast.error("Failed to load compliance forms. Please try again."))
            .finally(() => hideLoader());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedState]);

    // Only render a card for compliance types the backend actually returned
    // forms for, so e.g. a state with no PT form doesn't show a PT card at all.
    const cards = Object.keys(forms)
        .filter((key) => CARD_META[key])
        .map((key) => ({ key, ...CARD_META[key], stats: forms[key] || [] }));

    // Shops & Establishment Act ("SHOPS") is read from this exact same
    // `forms` object - the "SHOPS" bucket getComplianceFormsByState already
    // groups it into, same as every other compliance type - just rendered
    // through its own card component instead of the generic CARD_META loop
    // above, since its header needs the selected state's name and its forms
    // each carry their own filter set (see ShopsEstablishment.jsx).
    const selectedStateName =
        states.find((s) => String(s._ZoneID) === String(selectedState))?._Name || "";

    return (
        <>
            <div className="right d-flex justify-content-end mb-3">
                <div className="d-flex align-items-center">
                    <label htmlFor="complianceState" className="me-2 fw-bold">
                        State:
                    </label>

                    <select
                        id="complianceState"
                        name="complianceState"
                        className="form-control"
                        value={selectedState}
                        onChange={(e) => setSelectedState(e.target.value)}
                    >
                        {states.map((s) => (
                            <option key={s._ZoneID} value={s._ZoneID}>
                                {s._Name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="row">
                {/* Static option, independent of the selected state/backend
                    forms above - Form 24Q (TDS on Salaries) is filed
                    centrally, the same quarterly form regardless of state. */}
                <div className="col-lg-4 col-md col-sm mb-3">
                    <div className="card h-100 shadow-sm">
                        <div className="card-header d-flex justify-content-between fw-bold bg-primary text-white">
                            TDS - Form 138(24Q)

                            <Tooltip title="TDS - Form 138(24Q)">
                                <InfoOutlinedIcon className="cursor-pointer" />
                            </Tooltip>
                        </div>

                        <div className="card-body">
                            <Link
                                to="generate"
                                state={{
                                    form: "form24q",
                                    type: "24Q",
                                    label: "Form 138(24Q) - Quarterly TDS Statement",
                                }}
                                className="d-flex justify-content-between text-decoration-none"
                            >
                                <span>Quarterly TDS Statement</span>
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Static option, independent of the selected state/backend
                    forms above - Form A (Muster Roll under the Maternity
                    Benefit Act) is one statutory form used for every state;
                    only the selected employee's data changes, never the
                    form itself. Its Employee picker is still scoped to the
                    State selected above (carried via `state: selectedState`),
                    exactly like Form C/F/15's Employee picker. */}
                <div className="col-lg-4 col-md col-sm mb-3">
                    <div className="card h-100 shadow-sm">
                        <div className="card-header d-flex justify-content-between fw-bold bg-primary text-white">
                            Form A - Muster Roll

                            <Tooltip title="Form A - Muster Roll - Maternity Benefit Act">
                                <InfoOutlinedIcon className="cursor-pointer" />
                            </Tooltip>
                        </div>

                        <div className="card-body">
                            <Link
                                to="generate"
                                state={{
                                    form: "maternity-form-a",
                                    type: "MATERNITY_FORM_A",
                                    label: "Form A - Muster Roll - Maternity Benefit Act",
                                    state: selectedState,
                                }}
                                className="d-flex justify-content-between text-decoration-none"
                            >
                                <span>Muster Roll - Maternity Benefit Act</span>
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Static option, like the TDS card above - Form 12B is
                    generated for whichever employees are picked on the
                    Generate page, but its Employee picker is still scoped to
                    the State selected above, exactly like Form C/F/15's. */}
                <div className="col-lg-4 col-md col-sm mb-3">
                    <div className="card h-100 shadow-sm">
                        <div className="card-header d-flex justify-content-between fw-bold bg-primary text-white">
                            Form 12B

                            <Tooltip title="Form 12B - Statement of Salary/TDS Particulars">
                                <InfoOutlinedIcon className="cursor-pointer" />
                            </Tooltip>
                        </div>

                        <div className="card-body">
                            <Link
                                to="generate"
                                state={{
                                    form: "form12b",
                                    type: "12B",
                                    label: "Form 12B - Statement of Salary/TDS Particulars",
                                    state: selectedState,
                                }}
                                className="d-flex justify-content-between text-decoration-none"
                            >
                                <span>Statement of Salary/TDS Particulars</span>
                            </Link>
                        </div>
                    </div>
                </div>

                {/* The Payment of Bonus Act is a Central act with no
                    state-specific statutory variant, so Form C and Form D
                    are always shown regardless of the selected state - but
                    the employees they report on are still scoped to the
                    State selected above (carried via `state: selectedState`
                    below), exactly like the dynamically-rendered PT/LWF
                    cards further down. */}
                <div className="col-lg-4 col-md col-sm mb-3">
                    <div className="card h-100 shadow-sm">
                        <div className="card-header d-flex justify-content-between fw-bold bg-primary text-white">
                            Payment of Bonus Act - Central

                            <Tooltip title="Payment of Bonus Act - Central">
                                <InfoOutlinedIcon className="cursor-pointer" />
                            </Tooltip>
                        </div>

                        <div className="card-body">
                            <Link
                                to="generate"
                                state={{
                                    form: "bonus-form-c",
                                    type: "BONUS_FORM_C",
                                    label: "Form C - Bonus Paid To Employees - Payment of Bonus Act - Central",
                                    state: selectedState,
                                }}
                                className="d-flex justify-content-between text-decoration-none"
                            >
                                <span>Form C - Bonus Paid To Employees</span>
                            </Link>
                            <Link
                                to="generate"
                                state={{
                                    form: "bonus-form-d",
                                    type: "BONUS_FORM_D",
                                    label: "Form D - Annual Return bonus paid to employees - Payment of Bonus Act - Central",
                                    state: selectedState,
                                }}
                                className="d-flex justify-content-between text-decoration-none"
                            >
                                <span>Form D - Annual Return bonus paid to employees</span>
                            </Link>
                        </div>
                    </div>
                </div>

                {/* The Payment of Gratuity Act is likewise a Central act;
                    Form F is employee-specific like Form A - Muster Roll
                    above, and is likewise scoped to the selected State. */}
                <div className="col-lg-4 col-md col-sm mb-3">
                    <div className="card h-100 shadow-sm">
                        <div className="card-header d-flex justify-content-between fw-bold bg-primary text-white">
                            Payment of Gratuity Act - Central

                            <Tooltip title="Payment of Gratuity Act - Central">
                                <InfoOutlinedIcon className="cursor-pointer" />
                            </Tooltip>
                        </div>

                        <div className="card-body">
                            <Link
                                to="generate"
                                state={{
                                    form: "gratuity-form-f",
                                    type: "GRATUITY_FORM_F",
                                    label: "Form F - Nomination - Payment of Gratuity Act - Central",
                                    state: selectedState,
                                }}
                                className="d-flex justify-content-between text-decoration-none"
                            >
                                <span>Form F - Nomination</span>
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Form 15 (Factories Act, Section 79) is one common register
                    layout used for every state; the employees it's generated
                    for are scoped to the State selected above. */}
                <div className="col-lg-4 col-md col-sm mb-3">
                    <div className="card h-100 shadow-sm">
                        <div className="card-header d-flex justify-content-between fw-bold bg-primary text-white">
                            Form 15 - Register of Leave with Wages

                            <Tooltip title="Form 15 - Register of Leave with Wages - Factories Act">
                                <InfoOutlinedIcon className="cursor-pointer" />
                            </Tooltip>
                        </div>

                        <div className="card-body">
                            <Link
                                to="generate"
                                state={{
                                    form: "form-15",
                                    type: "FORM_15",
                                    label: "Form 15 - Register of Leave with Wages",
                                    state: selectedState,
                                }}
                                className="d-flex justify-content-between text-decoration-none"
                            >
                                <span>Register of Leave with Wages</span>
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Apprentices Act, 1961 - same "Other Reports" card/Generate
                    flow as Form 15 right above: a static card shown for
                    every state, whose employees are still scoped to the
                    State selected above (carried via `state: selectedState`
                    into the SAME shared /compliances/generate route/page
                    Form 15 uses - not a separate route or page). */}
                <div className="col-lg-4 col-md col-sm mb-3">
                    <div className="card h-100 shadow-sm">
                        <div className="card-header d-flex justify-content-between fw-bold bg-primary text-white">
                            Apprentices Act, 1961

                            <Tooltip title="Apprentices Act, 1961 - Apprentice/Training Report">
                                <InfoOutlinedIcon className="cursor-pointer" />
                            </Tooltip>
                        </div>

                        <div className="card-body">
                            <Link
                                to="generate"
                                state={{
                                    form: "apprentices-act",
                                    type: "APPRENTICES_ACT",
                                    label: "Apprentices Act, 1961 - Apprentice/Training Report",
                                    state: selectedState,
                                }}
                                className="d-flex justify-content-between text-decoration-none"
                            >
                                <span>Apprentice / Training Report</span>
                            </Link>
                        </div>
                    </div>
                </div>

                {cards.map((card) => (
                    <div
                        key={card.key}
                        className="col-lg-4 col-md col-sm mb-3"
                    >
                        <div className="card h-100 shadow-sm">
                            <div
                                className={`card-header d-flex justify-content-between fw-bold ${card.cls}`}
                            >
                                {card.title}

                                <Tooltip title={card.title}>
                                    <InfoOutlinedIcon className="cursor-pointer" />
                                </Tooltip>
                            </div>

                            <div className="card-body">
                                {card.stats.length === 0 && (
                                    <span className="text-muted">
                                        No forms configured for this state.
                                    </span>
                                )}

                                {card.stats.map((s) => (
                                    <Link
                                        to="generate"
                                        state={{
                                            form: card.form,
                                            type: s.form_name,
                                            label: s.form_name,
                                            state: selectedState,
                                        }}
                                        key={s.id}
                                        className="d-flex justify-content-between text-decoration-none"
                                    >
                                        <span>{s.form_name}</span>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Shops & Establishment Act - driven by the same State selector
                above, exactly like PT/LWF/MW/LLF: the card only renders at
                all when the selected state actually has a "SHOPS" bucket in
                `forms` (i.e. at least one SHOPS row for it) - the exact same
                rule the `cards` list above uses via CARD_META, never an
                empty/placeholder card for a state with none. */}
            {forms.SHOPS?.length > 0 && (
                <ShopsEstablishment
                    forms={forms.SHOPS}
                    stateId={selectedState}
                    stateName={selectedStateName}
                />
            )}
        </>
    );
};

export default Compliances;
